import { useEffect, useMemo, useState } from 'react';
import { useSession } from '@/app/providers/session-provider';
import { listCalendarEventsResult } from '../../calendar/services';
import {
  buildDashboardReadModel,
  buildDashboardClientsSummary,
  getDashboardAccountingDataResult,
  getDashboardAssignmentsDataResult,
  getDashboardClientsDataResult,
  getDashboardDealsDataResult,
} from '../services/dashboard.service';
import {
  buildDateRangeFromPreset,
  formatDateRangeLabel,
  getAccountingMetrics,
  getAssignmentSenderChartData,
  getAssignmentStats,
  getAssignmentStatusChartData,
  getAssignmentTypeChartData,
  getAssignmentUrgencyChartData,
  getBilancioChartData,
  getDealSummary,
  getFilteredAssignments,
  getIncassiClientiChartData,
  getLineChartData,
  getOverdueItems,
  getSpeseTipoChartData,
  getStaffPerformance,
  getStatsCards,
} from '../utils/dashboard.helpers';

export function useDashboardData() {
  const { logout } = useSession();
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [dateRange, setDateRange] = useState({
    from: null,
    to: new Date(),
  });

  const [incarichiDateRange, setIncarichiDateRange] = useState({
    from: null,
    to: new Date(),
  });

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isIncarichiCalendarOpen, setIsIncarichiCalendarOpen] = useState(false);

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        const [
          accountingResult,
          dealsResult,
          assignmentsResult,
          clientsResult,
          calendarEventsResult,
        ] = await Promise.all([
          getDashboardAccountingDataResult(),
          getDashboardDealsDataResult(),
          getDashboardAssignmentsDataResult(),
          getDashboardClientsDataResult(),
          listCalendarEventsResult(),
        ]);
        const boundaryResults = [
          accountingResult,
          dealsResult,
          assignmentsResult,
          clientsResult,
          calendarEventsResult,
        ];
        const authRequired = boundaryResults.some(
          (result) =>
            typeof result?.error === 'string' && result.error.endsWith('_AUTH_REQUIRED')
        );
        const sessionAcceptedByAnotherBoundary = boundaryResults.some(
          (result) => result?.ok === true
        );
        if (active && authRequired && !sessionAcceptedByAnotherBoundary) {
          logout();
        }
        if (active) {
          setRawData(
            buildDashboardReadModel({
              accountingResult,
              dealsResult,
              assignmentsResult,
              clientsResult,
              calendarEventsResult,
            })
          );
        }
      } catch (err) {
        if (active) setError(err?.message || 'Errore nel caricamento dashboard');
      } finally {
        if (active) setLoading(false);
      }
    }

    load();

    return () => {
      active = false;
    };
  }, []);

  const computed = useMemo(() => {
    if (!rawData) return null;

    const accounting = getAccountingMetrics(rawData.payments, rawData.expenses, dateRange, rawData);
    const overdueItems = getOverdueItems(rawData);
    const filteredAssignments = getFilteredAssignments(rawData.assignments, incarichiDateRange);
    const assignmentStats = getAssignmentStats(filteredAssignments);

    return {
      statsCards: getStatsCards({ ...rawData, overdueItems }),
      accountingUnavailable: rawData.accountingUnavailable === true,
      accounting,
      lineChartData: getLineChartData(accounting.receivedPayments, accounting.paidExpenses, dateRange),
      bilancioData: getBilancioChartData(accounting.incassati, accounting.uscitePagate),
      incassiClientiData: getIncassiClientiChartData(accounting.receivedPayments),
      speseTipoData: getSpeseTipoChartData(accounting.paidExpenses),

      filteredAssignments,
      overdueItems,
      overdueUnavailableModules: rawData.overdueUnavailableModules,
      incarichiStats: assignmentStats,
      incarichiPerStatusData: getAssignmentStatusChartData(assignmentStats),
      incarichiPerMittenteData: getAssignmentSenderChartData(filteredAssignments),
      incarichiPerTipoData: getAssignmentTypeChartData(filteredAssignments),
      incarichiPerUrgenzaData: getAssignmentUrgencyChartData(assignmentStats),
      staffPerformance: getStaffPerformance(filteredAssignments, rawData.staff),
      staffPerformanceStatus: rawData.assignmentsUnavailable ? 'unavailable' : 'available',
      staffPerformanceError: rawData.assignmentsError || '',

      dealSummary: getDealSummary(rawData),
      clientsSummary: buildDashboardClientsSummary({
        clients: rawData.clients,
        dealProposals: rawData.dealProposals,
        dealsInProgress: rawData.dealsInProgress,
        dealsCompleted: rawData.dealsCompleted,
      }),

      dateRangeLabel: formatDateRangeLabel(dateRange),
      incarichiDateRangeLabel: formatDateRangeLabel(incarichiDateRange),
    };
  }, [rawData, dateRange, incarichiDateRange]);

  return {
    rawData,
    computed,
    loading,
    error,

    dateRange,
    setDateRange,
    incarichiDateRange,
    setIncarichiDateRange,

    isCalendarOpen,
    setIsCalendarOpen,
    isIncarichiCalendarOpen,
    setIsIncarichiCalendarOpen,

    applyDatePreset: (preset) => {
      setDateRange(buildDateRangeFromPreset(preset));
      setIsCalendarOpen(false);
    },

    applyIncarichiPreset: (preset) => {
      setIncarichiDateRange(buildDateRangeFromPreset(preset));
      setIsIncarichiCalendarOpen(false);
    },
  };
}
